import { UUID } from 'crypto';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowageStore } from 'src/context/StowageContext';
import { useVesselStore } from 'src/context/VesselContext';
import { createRotationCargoList } from 'src/factory/Stowage/Rotation/RotationCargoFactory';
import { createRotationPortCallList } from 'src/factory/Stowage/Rotation/RotationPortCallFactory';
import { sortStowagePlanRotationPortCalls } from 'src/factory/Stowage/StowageOperationFactory';
import { RotationPortCallBM } from 'src/types/BusinessModel/Stowage/Rotation/RotationPortCallBM';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { compareRotations } from 'src/utils/stowage/stowagePlanComparison/compareRotationUtilsV2';
import { syncImosGuids } from 'src/utils/stowage/stowagePlanComparison/imosToStowageReferenceUtils';
import {
    addNamesToRotation,
    addProductAndfilterCargoWithoutProduct,
    addCargoAndFilterIncompleteCargoOperations,
    updateCargoOperations,
    isPortCallAfterFirstVoyageOrcaWebStowageImosSync,
    isCargoAfterFirstVoyageOrcaWebStowageImosSync,
} from 'src/utils/stowage/stowageRotationUtils';

export const useRotationResponseHandler = () => {
    const {
        setImosRotationPortCallList,
        setImosRotationCargoList,
        productList,
        portNameList,
        berthNameList,
        stowagePlanRotationPortCallList,
        stowagePlanRotationCargoList,
    } = useRotationStore();
    const { selectedVessel } = useVesselStore();
    const { activeStowagePlan, setImosSyncChanges } = useStowageStore();

    const onGetImosRotation = (result: any, additionalData?: any, checkForImosSync?: boolean) => {
        let _rotationPortCallBMList = result as RotationPortCallBM[];
        _rotationPortCallBMList.forEach((rotationPortCall) => {
            rotationPortCall.dataSource = 'IMOS';
            rotationPortCall.berthCalls.forEach((berthCall) => {
                berthCall.dataSource = 'IMOS';
                berthCall.cargoOperations.forEach((cargoOperation) => {
                    cargoOperation.dataSource = 'IMOS';
                });
            });
        });
        const _productList = additionalData?.productList ?? productList;
        const _portNameList = additionalData?.portNameList ?? portNameList;
        const _berthNameList = additionalData?.berthNameList ?? berthNameList;
        const _selectedVessel = (additionalData?.selectedVessel as Vessel) ?? selectedVessel;
        _rotationPortCallBMList = _rotationPortCallBMList.filter(
            (pc) =>
                +pc.voyageNumber >= _selectedVessel.firstVoyageOrcaWebStowageImosSync ||
                +pc.turnaroundVoyageNumber >= _selectedVessel.firstVoyageOrcaWebStowageImosSync
        );

        const _rotationCargoList = createRotationCargoList(_rotationPortCallBMList);
        const _filteredRotationCargoList = addProductAndfilterCargoWithoutProduct(_rotationCargoList, _productList);

        let _rotationPortCallList = createRotationPortCallList(_rotationPortCallBMList);
        _rotationPortCallList = sortStowagePlanRotationPortCalls(_rotationPortCallList);
        addCargoAndFilterIncompleteCargoOperations(_rotationPortCallList, _filteredRotationCargoList);
        addNamesToRotation(_rotationPortCallList, _portNameList, _berthNameList);
        const _cargoOperations = _rotationPortCallList.flatMap((portCall) => portCall.berthCalls.flatMap((berthCall) => berthCall.cargoOperations));
        updateCargoOperations(_rotationPortCallList, _cargoOperations);

        _filteredRotationCargoList.forEach((cargo) => {
            cargo.dataSource = 'IMOS';
        });

        setImosRotationCargoList([..._filteredRotationCargoList]);
        setImosRotationPortCallList([..._rotationPortCallList]);

        if (checkForImosSync) {
            const _stowagePlanGuid = (additionalData?.stowagePlanGuid as UUID) ?? activeStowagePlan.stowagePlanGuid;
            const _stowagePlanTransitOperations =
                (additionalData?.stowagePlanTransitOperations as RotationPortCall[]) ?? activeStowagePlan.transitOperations;
            const _existingRotation = (additionalData?.stowagePlanRotationPortCallList as RotationPortCall[]) ?? stowagePlanRotationPortCallList;
            const _existingCargoes = (additionalData?.stowagePlanRotationCargoList as RotationCargo[]) ?? stowagePlanRotationCargoList;
            syncImosGuids(_rotationPortCallList, _existingRotation, _filteredRotationCargoList, _existingCargoes);

            const stowagePlanChanges = compareRotations({
                stowagePlanGuid: _stowagePlanGuid,
                stowagePlanTransitOperations: _stowagePlanTransitOperations,
                existingRotation: _existingRotation.filter((pc) =>
                    isPortCallAfterFirstVoyageOrcaWebStowageImosSync(pc, _selectedVessel.firstVoyageOrcaWebStowageImosSync)
                ),
                incomingRotation: _rotationPortCallList.filter((pc) =>
                    isPortCallAfterFirstVoyageOrcaWebStowageImosSync(pc, _selectedVessel.firstVoyageOrcaWebStowageImosSync)
                ),
                existingCargoes: _existingCargoes.filter((c) =>
                    isCargoAfterFirstVoyageOrcaWebStowageImosSync(c, _selectedVessel.firstVoyageOrcaWebStowageImosSync)
                ),
                incomingCargoes: _filteredRotationCargoList.filter((c) =>
                    isCargoAfterFirstVoyageOrcaWebStowageImosSync(c, _selectedVessel.firstVoyageOrcaWebStowageImosSync)
                ),
            });
            setImosSyncChanges([...stowagePlanChanges]);
        }
    };

    return { onGetImosRotation };
};
